/**
 * ui/services/social.ts
 *
 * Sharing a replay by link, and the boards of who solved each puzzle shared
 * by link. The player is known to the server by their account when signed
 * in, otherwise by this device's profile id, and shown by the account's
 * username or the name picked on this device.
 */
import type { ReplayRecord } from '../../ports/replays.js';
import type { PuzzleBoard, ReplayUpload, SocialApiPort, SubmitResult } from '../../ports/social.js';
import { HttpSocialApi } from '../../adapters/http-social.js';
import { AccountService } from './account.js';
import { ProfileService } from './profile.js';
import { ReplayService } from './replays.js';
import { isInAppPath } from '../../utils/paths.js';

/** The server's form of a saved replay (it adds the puzzle details itself). */
export function toUpload(record: Pick<ReplayRecord, 'puzzleId' | 'solveTimeMs' | 'mode' | 'moveLog' | 'players'>): ReplayUpload {
  return {
    puzzleId: record.puzzleId,
    solveTimeMs: Math.round(record.solveTimeMs),
    mode: record.mode,
    moveLog: record.moveLog.map((move) => ({
      indices: move.indices,
      values: move.values,
      timestampMs: Math.max(0, move.timestampMs),
      ...(move.playerId ? { playerId: move.playerId } : {}),
    })),
    ...(record.mode === 'multiplayer' && record.players
      ? { players: record.players.map(({ id, name, color }) => ({ id, name, color: hexColor(color) })) }
      : {}),
  };
}

/** A game route's exit path (`?back=`, in-app paths only). */
export function gameExitHref(search: string): string {
  const back = new URLSearchParams(search).get('back') ?? '';
  return isInAppPath(back) ? back : '/puzzles';
}

/** The server keeps player colors as hex; other CSS colors become a neutral grey. */
function hexColor(color: string): string {
  return /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : '#888888';
}

/** Names the server accepts from guests (no symbols that could pass for the signed-in ✓). */
export const GUEST_NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} ._'-]*$/u;

/** A guest name reduced to what the server accepts ('' when nothing is left). */
export function guestName(name: string): string {
  const cleaned = name.replace(/[^\p{L}\p{N} ._'-]/gu, '').trim().replace(/^[ ._'-]+/, '').slice(0, 24);
  return GUEST_NAME_PATTERN.test(cleaned) ? cleaned : '';
}

export function watchUrl(sharedReplayId: string, origin = globalThis.location?.origin ?? ''): string {
  return `${origin}/watch/${encodeURIComponent(sharedReplayId)}`;
}


export class SocialService {
  static readonly instance = new SocialService(
    new HttpSocialApi(() => ({ token: AccountService.instance.token(), profileId: ProfileService.instance.get().id })),
  );

  constructor(
    readonly api: SocialApiPort,
    private readonly _replays: Pick<ReplayService, 'getReplay' | 'markShared'> = ReplayService.instance,
    private readonly _account: Pick<AccountService, 'current'> = AccountService.instance,
    private readonly _profile: Pick<ProfileService, 'get' | 'update'> = ProfileService.instance,
  ) {}

  /** The name shown on boards and shared replays ('' until the player picks one). */
  playerName(): string {
    return this._account.current()?.username ?? this._profile.get().name.trim();
  }

  /** Signed-in players use their username; others can set the name kept on this device. */
  setPlayerName(name: string): void {
    this._profile.update({ name: name.trim().slice(0, 24) });
  }

  /** Shares a saved replay (once; later calls reuse the link). Returns the server replay id. */
  async shareReplay(localReplayId: string): Promise<string> {
    const record = await this._requireReplay(localReplayId);
    if (record.sharedId) return record.sharedId;
    const id = await this.api.uploadReplay(toUpload(record), this._nameOrGuest());
    await this._replays.markShared(record.id, id);
    return id;
  }

  /** A shared puzzle's board (null: it has none). */
  board(puzzleId: string): Promise<PuzzleBoard | null> {
    return this.api.getBoard(puzzleId);
  }

  /** Puts a solo solve on its shared puzzle's board. */
  submit(record: Pick<ReplayRecord, 'puzzleId' | 'solveTimeMs' | 'mode' | 'moveLog' | 'players'>): Promise<SubmitResult> {
    return this.api.submitToBoard(toUpload(record), this._nameOrGuest());
  }

  /** The name to send: a guest's is cleaned to what the server accepts. */
  private _nameOrGuest(): string {
    const name = this.playerName();
    if (this._account.current()) return name;
    return guestName(name) || 'Guest';
  }

  private async _requireReplay(id: string): Promise<ReplayRecord> {
    const record = await this._replays.getReplay(id);
    if (!record) throw new Error("This replay isn't on this device");
    return record;
  }
}
